/* eslint-disable react/prop-types, react/jsx-props-no-spreading */
import React from 'react';
import { useParams } from 'react-router-dom';
import { Spin } from 'antd';
import noop from 'lodash/noop';

import { postBy, formatArticleError } from '@utils';
import { ContentModuleModal, SortableList } from '@molecules';
import { Typography } from '@atoms';
import { NO_MARGIN_BOTTOM } from '@constants';
import { useArticlesByIds } from '@api/hooks';
import { getImage } from '@utils/media';
import BackgroundSelect from '@organisms/BackgroundSelect/index';

import Form from '../Form';

import HeroSlidesForm from './HeroSlidesForm';
import SlideItem from './SlideItem';
import CarouselItem from './item';
import { SLIDES, HERO_CAROUSEL_INITS, FORM_SLIDES, detectMode, MODES } from './constant';
import s from './styles.module.less';

const HeroCarousel = ({
  visible = true,
  previewImg,
  onSubmit,
  hide,
  onDelete,
  initialValues: initData,
  errors,
  isUltimateDesign,
}) => {
  const [form] = Form.useForm(FORM_SLIDES);
  const { locale } = useParams();

  const initialValues = React.useMemo(() => form.deserialize(initData || HERO_CAROUSEL_INITS), [form, initData]);

  const { articles, isFetching } = useArticlesByIds(
    (initialValues?.slides || []).filter(({ articleId }) => !!articleId).map(({ articleId }) => articleId),
    locale,
    { passErrors: true },
  );

  const [slides, setSlides] = React.useState(initialValues[SLIDES.name]);

  const syncSlides = React.useCallback(
    (_slides) => {
      form.setFields([{ name: SLIDES.name, value: _slides }]);
      setSlides(_slides);
    },
    [form, setSlides],
  );

  React.useEffect(() => {
    if (visible) {
      const _slides = initialValues.slides.map((slide) => {
        // slide also could be error if in useArticlesByIds article errored
        const { articleId } = slide;
        return articleId ? { article: articles.find((article) => article.id === articleId), ...slide } : slide;
      });
      syncSlides(_slides);
    }
    // articles are not here just because they change every time loop renders
    // instead we'll stick to isFetching which is scalar and compared by value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, form, visible, initialValues, syncSlides]);

  const handleClose = () => {
    form.resetFields();
    hide();
  };

  const handleSubmit = (values) => {
    onSubmit(values);
    handleClose();
  };

  const handleAdd = React.useCallback((slide) => syncSlides([slide, ...(form.getFieldValue(SLIDES.name) || [])]), [
    form,
    syncSlides,
  ]);

  const handleOnValuesChange = ({ [FORM_SLIDES.BACKGROUND.name]: backgroundImage }) => {
    if (backgroundImage) {
      form.setFieldsValue({
        [FORM_SLIDES.BACKGROUND.name]: { id: backgroundImage.id, edited: backgroundImage.edited },
      });
    }
  };
  return (
    <ContentModuleModal
      visible={visible}
      title="Hero carousel"
      subtitle="Add slides to appear in the page’s banner carousel. You can choose up to 6 slides."
      okText={onDelete ? 'SAVE CHANGES' : 'ADD HERO CAROUSEL'}
      onCancel={handleClose}
      onSubmit={form.submit}
      onDelete={onDelete}
      heroImageSrc={previewImg}
      errors={errors}
    >
      <Typography.Title level={3}>Add a slide</Typography.Title>
      <HeroSlidesForm onSubmit={handleAdd} className={s.addForm} isUltimateDesign={isUltimateDesign} />
      <Spin spinning={isFetching}>
        <SortableList
          style={NO_MARGIN_BOTTOM}
          withPlaceholder
          value={slides}
          label="Current slides"
          withCount
          onChange={syncSlides}
        >
          {(value, { edit, isEdit, save, buttons }) =>
            isEdit ? (
              <HeroSlidesForm
                onSubmit={save}
                initialValues={value}
                buttons={buttons}
                isUltimateDesign={isUltimateDesign}
              />
            ) : (
              <SlideItem
                image={getImage(value.article || value, value.article ? 'featureImage' : 'image')}
                description={value.article ? postBy(value.article) : value.subtitle}
                // consider slide with no articleId and videoUrl set is manually added
                handleEdit={detectMode(value) === MODES.MANUAL ? edit : undefined}
              >
                {/* value.article can be error because of useArticlesByIds is passing error if some article has failed to load */}
                {value.article instanceof Error ? formatArticleError(value.article) : (value.article || value)?.title}
              </SlideItem>
            )
          }
        </SortableList>

        <Form
          form={form}
          onSerializedFinish={handleSubmit}
          hideRequiredMark
          initialValues={initialValues}
          onValuesChange={handleOnValuesChange}
        >
          <Form.List className={s.listItem} {...SLIDES} style={{ marginBottom: 20 }}>
            {noop}
          </Form.List>
          {isUltimateDesign && (
            <BackgroundSelect
              isImgBackground={!!initData?.desktopBackgroundImageId && !!initData?.mobileBackgroundImageId}
              background={FORM_SLIDES.BACKGROUND}
              mobileBackground={FORM_SLIDES.MOBILE_BACKGROUND}
              backgroundColours={FORM_SLIDES.BACKGROUND_COLOUR}
            />
          )}
        </Form>
      </Spin>
    </ContentModuleModal>
  );
};

HeroCarousel.Item = CarouselItem;

export default HeroCarousel;
